import React, { useState, useEffect } from 'react';

const BoxModal = ({ box, isOpen, onClose }) => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    guestCount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSelectedTier(box?.tiers?.[0] || null);
      setSelectedAddons([]);
      setShowOrderForm(false);
      setSubmitSuccess(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, box]);

  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.name === addon.name);
      if (exists) {
        return prev.filter(a => a.name !== addon.name);
      }
      return [...prev, addon];
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call Netlify function to create Stripe Checkout session
      const response = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          box: {
            name: box.name,
            vibe: box.vibe,
          },
          tier: {
            name: selectedTier?.name,
            price: selectedTier?.price,
            includes: selectedTier?.includes,
          },
          addons: selectedAddons.map(a => ({
            name: a.name,
            price: a.price,
          })),
          customer: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            eventDate: formData.eventDate,
            guestCount: formData.guestCount,
            message: formData.message,
          },
        }),
      });

      const data = await response.json();

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  const calculateTotal = () => {
    if (!selectedTier) return '$0';
    const tierPrice = parseInt(selectedTier.price.replace(/[^0-9]/g, '')) || 0;
    const addonsTotal = selectedAddons.reduce((sum, addon) => {
      const price = parseInt(addon.price.replace(/[^0-9]/g, '')) || 0;
      return sum + price;
    }, 0);
    return `$${tierPrice + addonsTotal}+`;
  };

  if (!isOpen || !box) return null;

  return (
    <div className={`box-detail-modal ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          ×
        </button>

        {submitSuccess ? (
          <div className="form-success" style={{ margin: '2rem' }}>
            <h3>🎉 Order Request Received!</h3>
            <p>Thank you for your interest in {box.name}! We'll be in touch within 24 hours to confirm your order and discuss details.</p>
            <button className="btn btn-primary mt-lg" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="service-detail-content">
              <div className="service-detail-image">
                <img src={box.image} alt={box.name} />
              </div>
              <div className="service-detail-info">
                <h3>{box.name}</h3>
                <p className="box-vibe" style={{ fontStyle: 'italic', color: '#c4a574' }}>
                  "{box.tagline}"
                </p>
                <p>{box.vibe}</p>

                <div style={{ margin: '1rem 0', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {box.palette && box.palette.map((color, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        border: '1px solid #ddd'
                      }}></span>
                      <small>{box.paletteNames?.[idx]}</small>
                    </div>
                  ))}
                </div>

                <p><strong>Guest Range:</strong> {box.guestRange}</p>
                <p><strong>Setup Time:</strong> {box.setupTime}</p>
                <p><strong>Ideal For:</strong> {box.idealFor}</p>
              </div>
            </div>

            <div style={{ padding: '2rem' }}>
              <div className="service-includes">
                <h5>What's Included (Luxe Box)</h5>
                <ul>
                  {box.includes && box.includes.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              {!showOrderForm ? (
                <>
                  <h5 style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    letterSpacing: '0.05em', 
                    textTransform: 'uppercase', 
                    color: '#9a7b4f',
                    marginBottom: '1rem'
                  }}>
                    Select Your Tier
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    {box.tiers && box.tiers.map((tier, idx) => (
                      <label
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '1rem',
                          padding: '1rem',
                          background: selectedTier?.name === tier.name ? '#f5ebe8' : '#faf8f5',
                          borderRadius: '8px',
                          border: selectedTier?.name === tier.name ? '2px solid #c4a574' : '2px solid transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <input
                          type="radio"
                          name="tier"
                          checked={selectedTier?.name === tier.name}
                          onChange={() => setSelectedTier(tier)}
                          style={{ marginTop: '4px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong>{tier.name}</strong>
                            <span style={{ color: '#c4a574', fontWeight: '600' }}>{tier.price}</span>
                          </div>
                          <small style={{ color: '#666' }}>{tier.includes}</small>
                        </div>
                      </label>
                    ))}
                  </div>

                  {box.addons && box.addons.length > 0 && (
                    <>
                      <h5 style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        letterSpacing: '0.05em', 
                        textTransform: 'uppercase', 
                        color: '#9a7b4f',
                        marginBottom: '1rem'
                      }}>
                        Optional Add-ons
                      </h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                        {box.addons.map((addon, idx) => (
                          <label
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.75rem 1rem',
                              background: selectedAddons.find(a => a.name === addon.name) ? '#f5ebe8' : '#faf8f5',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!selectedAddons.find(a => a.name === addon.name)}
                              onChange={() => handleAddonToggle(addon)}
                            />
                            <span style={{ flex: 1 }}>{addon.name}</span>
                            <span style={{ color: '#c4a574', fontWeight: '600' }}>{addon.price}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '1rem',
                    background: '#2d2d2d',
                    color: '#fff',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <span style={{ fontWeight: '600' }}>Estimated Total:</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#c4a574' }}>
                      {calculateTotal()}
                    </span>
                  </div>

                  <button 
                    className="btn btn-gold btn-lg"
                    style={{ width: '100%' }}
                    onClick={() => setShowOrderForm(true)}
                  >
                    Continue to Order
                  </button>
                </>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h5 style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    letterSpacing: '0.05em', 
                    textTransform: 'uppercase', 
                    color: '#9a7b4f',
                    marginBottom: '1rem'
                  }}>
                    Your Information
                  </h5>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="modal-name">Full Name *</label>
                      <input
                        type="text"
                        id="modal-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="modal-email">Email Address *</label>
                      <input
                        type="email"
                        id="modal-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="modal-phone">Phone Number</label>
                      <input
                        type="tel"
                        id="modal-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="modal-date">Event Date *</label>
                      <input
                        type="date"
                        id="modal-date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="modal-guests">Expected Guest Count</label>
                    <input
                      type="number"
                      id="modal-guests"
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleInputChange}
                      placeholder="e.g., 10"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="modal-message">Additional Notes</label>
                    <textarea
                      id="modal-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your event, any customization requests, or questions..."
                      rows="4"
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '1rem',
                    background: '#f5ebe8',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <div>
                      <strong>{box.name}</strong>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        {selectedTier?.name} • {selectedAddons.length} add-on(s)
                      </div>
                    </div>
                    <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#c4a574' }}>
                      {calculateTotal()}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowOrderForm(false)}
                      style={{ flex: 1 }}
                    >
                      Back
                    </button>
                    <button 
                      type="submit"
                      className={`btn btn-gold ${isSubmitting ? 'btn-loading' : ''}`}
                      disabled={isSubmitting}
                      style={{ flex: 2 }}
                    >
                      {isSubmitting ? 'Redirecting to Payment...' : 'Proceed to Payment'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BoxModal;
